import { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import recStyles from './CallRecordingModal.module.scss'

const SPEEDS = [1, 1.25, 1.5, 1.75, 2]

const fmt = (sec) => {
  if (!isFinite(sec) || isNaN(sec)) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const ChunkPlayer = ({ chunk, chunkKey, activeKey, onPlay }) => {
  const audioSrc = typeof chunk === 'string' ? chunk : chunk?.url
  const chunkTimestamp = typeof chunk === 'object' ? chunk?.timestamp : null

  const audioRef = useRef(null)
  const isPlayingRef = useRef(false)

  const [speed, setSpeed] = useState(1)
  const [corrupt, setCorrupt] = useState(false)
  const [loading, setLoading] = useState(true)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  // Pause this player whenever a different chunk becomes active
  useEffect(() => {
    if (activeKey !== undefined && activeKey !== chunkKey && isPlayingRef.current) {
      audioRef.current?.pause()
      setPlaying(false)
      isPlayingRef.current = false
    }
  }, [activeKey, chunkKey])

  // On mount: load src, then play→pause to kick off full buffering.
  // This also forces the browser to fetch enough of the file to report duration.
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.src = audioSrc
    audio.load()

    const trigger = audio.play()
    if (trigger && typeof trigger.then === 'function') {
      trigger.then(() => audio.pause()).catch(() => {
        // Autoplay blocked — audio.load() still buffers in the background
      })
    } else {
      audio.pause()
    }

    return () => {
      audio.pause()
      audio.src = ''
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc])

  const handleCanPlayThrough = () => {
    const audio = audioRef.current
    if (!audio) return

    if (isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration)
      setLoading(false)
    } else {
      // WebM files often lack duration metadata — seek to end to force the
      // browser to scan the whole file; real duration fires via onDurationChange
      audio.currentTime = 1e101
    }
  }

  const handleDurationChange = () => {
    const audio = audioRef.current
    if (!audio) return
    if (isFinite(audio.duration) && audio.duration > 0) {
      setDuration(audio.duration)
      // Snap back to start after the forced seek-to-end
      if (audio.currentTime > 0 && !isPlayingRef.current) audio.currentTime = 0
      setLoading(false)
    }
  }

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime)
  }

  const handleEnded = () => {
    setPlaying(false)
    isPlayingRef.current = false
    setCurrentTime(0)
    if (audioRef.current) audioRef.current.currentTime = 0
  }

  const handleAudioError = () => {
    setCorrupt(true)
    setLoading(false)
  }

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
      isPlayingRef.current = false
    } else {
      audio.playbackRate = speed
      audio.play().then(() => {
        setPlaying(true)
        isPlayingRef.current = true
        onPlay?.(chunkKey)
      }).catch(() => {})
    }
  }

  const handleSeek = (e) => {
    const audio = audioRef.current
    if (!audio) return
    const val = parseFloat(e.target.value)
    audio.currentTime = val
    setCurrentTime(val)
  }

  const handleSpeedChange = (s) => {
    setSpeed(s)
    if (audioRef.current) audioRef.current.playbackRate = s
  }

  if (corrupt) return null

  return (
    <tr>
      <td className="w-25">
        <span className={recStyles.chunkLabel}>Chunk {chunkKey}</span>
        {chunkTimestamp && (
          <span className={recStyles.chunkTime}>
            {moment(chunkTimestamp).format('MMM DD YYYY, HH:mm:ss')}
          </span>
        )}
      </td>

      <td className={recStyles.playerCell}>
        <audio
          ref={audioRef}
          preload="auto"
          onCanPlayThrough={handleCanPlayThrough}
          onDurationChange={handleDurationChange}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onError={handleAudioError}
        />

        {loading ? (
          <span style={{ fontSize: 12, color: '#9393aa' }}>Buffering audio…</span>
        ) : (
          <>
            <div className={recStyles.playerRow}>
              <button
                className={recStyles.playBtn}
                onClick={togglePlay}
                title={playing ? 'Pause' : 'Play'}
              >
                {playing ? '⏸' : '▶'}
              </button>

              <input
                type="range"
                className={recStyles.seekBar}
                min={0}
                max={duration || 0}
                step={0.1}
                value={currentTime}
                onChange={handleSeek}
              />

              <div className={recStyles.speedGroup}>
                {SPEEDS.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleSpeedChange(s)}
                    className={`${recStyles.speedBtn} ${speed === s ? recStyles.speedBtnActive : ''}`}
                  >
                    {s}x
                  </button>
                ))}
              </div>
            </div>

            <div className={recStyles.timeRow}>
              <span>{fmt(currentTime)}</span>
              <span>{fmt(duration)}</span>
            </div>
          </>
        )}
      </td>
    </tr>
  )
}

export default ChunkPlayer
